import { HERO_BADGE_POSITIONS, ruStepIllustrations } from "./shared";
import { getScriptarioLocalizedPath } from "@/config/locales";
import type { LandingContent } from "./types";
import { intlFileHostingCopy } from "@/lib/file-hosting-sources-intl";

const locale = "it";
const seoFaqText = `La trascrizione audio in testo online aiuta a trasformare registrazioni, riunioni, interviste, lezioni, podcast e video in testo consultabile. Scriptario combina trascrizione IA, marcatori temporali, riconoscimento dei relatori, riassunti e report per ridurre il lavoro manuale dopo ogni registrazione.`;

export const scriptarioItContent: LandingContent = intlFileHostingCopy({
  nav: {
    pageLinks: [{ label: "Funzionalità", href: getScriptarioLocalizedPath("/features", locale) }, { label: "Prezzi", href: getScriptarioLocalizedPath("/pricing", locale) }, { label: "Blog", href: getScriptarioLocalizedPath("/blog", locale) }, { label: "Contatti", href: getScriptarioLocalizedPath("/contacts", locale) }],
    landingLinks: [{ label: "Funzionalità", href: "#features" }, { label: "Prezzi", href: "#pricing" }, { label: "Blog", href: getScriptarioLocalizedPath("/blog", locale) }, { label: "Contatti", href: getScriptarioLocalizedPath("/contacts", locale) }],
  },
  header: { dashboard: "Dashboard", signIn: "Accedi", startFree: "Inizia gratis", openMenu: "Apri menu", closeMenu: "Chiudi menu", menu: "Menu" },
  hero: {
    title: <>Trascrizione audio e video<br className="hidden desktop:inline" /> in testo con <span style={{ color: "var(--brand-primary)" }}>IA</span></>,
    subtitle: <>Riunioni, podcast, lezioni, interviste e webinar diventano testo pronto da usare<br className="hidden desktop:inline" /> in pochi minuti. Provalo gratis</>,
    badges: ["99% di precisione", "Marcatori temporali", "Qualsiasi formato", "Chat e report IA", "Relatori", "1 ora in testo in 3 min"].map((label, i) => ({ label, ...HERO_BADGE_POSITIONS[i] })),
    trustText: "Di noi si fidano moltissimi altri partners",
  },
  upload: {
    fileTab: "File",
    linkTab: "Link",
    dragOrUpload: "Trascina o carica un file",
    chooseFiles: "Scegli file",
    submit: "Invia",
    removeFile: "Rimuovi file",
    tabsLabel: "Metodo di caricamento",
    linkPlaceholder: "Incolla un link YouTube, Zoom o Google Drive...",
    linkPlaceholderMobile: "Incolla un link",
    supportedSources: "Supportati:",
    supportedFiles: "Sono supportati file audio e video in qualsiasi formato",
    comingSoon: "Scriptario sarà disponibile a breve",
    unsupportedFile: "Sono supportati solo file audio e video",
    uploadError: "Errore di caricamento",
    retryUpload: "Riprova",
    finishingUpload: "Completamento del caricamento",
  },
  cookie: {
    textLine1: "Usiamo cookie necessari per far funzionare il sito.",
    textLine2: "I cookie opzionali vengono usati solo con il tuo consenso.",
    accept: "Accetta",
    reject: "Rifiuta",
  },
  how: {
    title: "Come funziona",
    subtitle: "Dalla registrazione al testo finito, in tre passaggi",
    steps: [
      { step: "Passo 1", title: "Carica una registrazione", description: "Trascina un file audio o video in qualsiasi formato. Oppure incolla un link YouTube, Zoom o Google Drive.", illustration: ruStepIllustrations[0], className: "" },
      { step: "Passo 2", title: "Si converte in pochi minuti", description: "L'IA riconosce il parlato, aggiunge punteggiatura, separa i relatori e crea marcatori temporali. Un'ora di audio richiede circa 3 minuti.", illustration: ruStepIllustrations[1], className: "overflow-hidden" },
      { step: "Passo 3", title: "Lavora sul risultato", description: "Modifica il testo nel browser, chiedi all'IA di creare un report o scarica PDF, Word, TXT o sottotitoli SRT.", illustration: ruStepIllustrations[2], className: "overflow-hidden" },
    ],
  },
  stats: { users: "utenti già con noi", hours: "ore trascritte", rating: "valutazione media", speed: "lavoro più veloce con le registrazioni" },
  features: {
    title: "Funzionalità",
    subtitle: "Tutto ciò che serve per lavorare con audio e video",
    cards: [
      { iconName: "feature-target", title: "99% di precisione", description: "L'IA crea riassunti, evidenzia punti chiave e pianifica liste per attività quando ne hai bisogno." },
      { iconName: "feature-speedometer", title: "Elaborazione rapida", description: "Un'ora di registrazione viene trascritta in circa 3 minuti. Niente attese infinite." },
      { iconName: "feature-translate", title: "99 lingue", description: "Italiano, inglese, spagnolo, tedesco, francese e molte altre lingue con rilevamento automatico." },
    ],
    aiReports: { title: "Report IA", description: "Riassunti, punti chiave e attività. L'IA crea report dalla trascrizione su richiesta." },
    aiChat: { title: "Chat IA", description: "Fai domande sulla trascrizione. L'IA trova le informazioni rilevanti e risponde direttamente." },
    speakers: { title: "Separazione relatori", description: "Rileva automaticamente chi parla. Ogni riga ha un'etichetta e un marcatore temporale." },
    meetingAssistant: { title: "Assistente riunioni", description: "Registra Google Meet, Zoom o altre call. L'assistente trascrive e prepara note automaticamente." },
    telegramBot: { title: "Versione mobile", description: "Apri Scriptario dal telefono per caricare registrazioni, leggere trascrizioni e lavorare sui risultati ovunque." },
    linkTranscription: { title: "Trascrizione da link", description: "Incolla un link YouTube, di una registrazione Zoom o di Google Drive e lo scaricheremo e trascriveremo automaticamente." },
    smallCards: [
      { iconName: "feature-text-aa", title: "Punteggiatura automatica", description: "Punti, virgole e paragrafi vengono aggiunti automaticamente. Il testo è pronto da leggere." },
      { iconName: "feature-file", title: "Tutti i formati", description: "MP3, WAV, MP4, MOV, MKV, OGG, M4A e molti altri." },
      { iconName: "feature-download", title: "Esporta in ogni formato", description: "Scarica trascrizioni come PDF, DOCX, TXT o sottotitoli SRT." },
    ],
  },
  personas: {
    title: "Per chi è",
    subtitle: "Team e professionisti risparmiano ore ogni settimana",
    items: [
      { image: "/assets/personas/manager.png", title: "Manager e dirigenti", description: "Verbali automatici e riassunti di riunioni. L'IA evidenzia decisioni e attività." },
      { image: "/assets/personas/entrepreneur.png", title: "Imprenditori", description: "Trascrivi chiamate con clienti, discovery interview e trattative. Ogni dettaglio resta ricercabile." },
      { image: "/assets/personas/creator.png", title: "Creator e blogger", description: "Trasforma podcast e video in post, articoli e script. Genera sottotitoli per contenuti senza audio." },
      { image: "/assets/personas/researcher.png", title: "Ricercatori", description: "Trascrivi interviste e focus group in pochi minuti invece che in ore. I report IA fanno emergere insight e temi." },
      { image: "/assets/personas/journalist.png", title: "Giornalisti e media", description: "Trascrivi interviste e conferenze stampa in pochi minuti. Marcatori temporali e relatori aiutano a trovare citazioni." },
      { image: "/assets/personas/student.png", title: "Studenti e docenti", description: "Trasforma lezioni e seminari in appunti pronti. Cerca per parola chiave invece di riascoltare tutto." },
    ],
  },
  pricing: {
    title: "Trascrivi ore di registrazioni in pochi minuti",
    reviewLabel: "su Trustpilot",
    usersLabel: "utenti",
    hoursLabel: "ore trascritte",
    month: "Mese",
    year: "Anno",
    yearBadge: "-50%",
    periodYearTitle: "Annuale",
    periodMonthTitle: "Mensile",
    yearTotalNote: "in un unico pagamento annuale",
    plans: [
      { planId: "basic", title: "Essential", description: "per attività ricorrenti", ctaLabel: "Abbonati" },
      { planId: "standard", title: "Professional", description: "per la maggior parte dei lavori", ctaLabel: "Abbonati", featured: true, badge: "Popolare" },
      { planId: "expert", title: "Premium", description: "per grandi volumi", ctaLabel: "Abbonati" },
    ],
    tryTitle: "Vuoi solo provare?",
    tryDescription: "15 minuti gratis al mese, per sempre",
    startFree: "Inizia gratis ->",
    includedTitle: "Incluso in ogni piano:",
    commonFeatures: ["Registrazione riunioni", "Report e chat IA", "File fino a 5 GB", "Relatori e marcatori temporali"],

    commonFeatureIcons: ["meetings", "ai", "files", "speakers"],
    cancel: "Annulla quando vuoi",
    refund: "Rimborso entro 7 giorni",
    perMonth: "/mese",
    perYear: "/anno",
    annualBilling: "spesa annuale",
    monthlyBilling: "Spesa mensile",
    overagePrefix: "oltre il limite - ",
    overageSuffix: "/min",
    minutesPerMonth: (minutes) => `${minutes.toLocaleString("it-IT")} minuti al mese`,
  },
  testimonials: {
    title: "Recensioni",
    subtitle: "Cosa dicono gli utenti",
    items: [
      { quote: "Trascrivo diverse interviste alla settimana. Ora ricevo il testo in pochi minuti e posso vedere subito chi ha detto cosa.", avatar: "/assets/testimonial-1.jpg", name: "Sarah Mitchell", role: "Editor digitale" },
      { quote: "Carico le lezioni registrate e ottengo una trascrizione completa con paragrafi e punteggiatura. Riutilizzo il materiale per appunti e dispense.", avatar: "/assets/testimonial-2.jpg", name: "Emily Carter", role: "Docente" },
      { quote: "Uso Scriptario per verbali e chiamate con clienti. L'IA evidenzia attività e decisioni automaticamente.", avatar: "/assets/testimonial-3.jpg", name: "Michael Brooks", role: "Project manager" },
    ],
  },
  faq: {
    title: "Domande frequenti",
    subtitle: "Risposte alle domande principali sul servizio",
    items: [
      { question: "Lavorate con aziende e team?", answer: "Sì. Aiutiamo team di ricerca e aziende a scegliere il volume di trascrizione adatto. Scrivi a hello@scriptario.com." },
      { question: "Quali lingue supporta Scriptario?", answer: "La trascrizione è disponibile in 99 lingue, incluso italiano, inglese, spagnolo, tedesco e francese. La lingua può essere rilevata automaticamente." },
      { question: "Come vengono indicati i relatori?", answer: "Il servizio rileva e separa automaticamente i relatori. I nomi possono essere modificati manualmente nell'editor." },
      { question: "Come proteggete file e dati?", answer: "I file e le trascrizioni sono cifrati e conservati su infrastruttura protetta. Non vendiamo i tuoi dati." },
      { question: "Ci sono limiti di dimensione o durata?", answer: "Puoi caricare file fino a 5 GB. La durata massima della registrazione è 12 ore." },
      { question: "In quali formati posso esportare?", answer: "Le trascrizioni possono essere esportate come TXT. Altri formati sono disponibili nel workflow del prodotto." },
      { question: "Che cos'è Scriptario e come funziona?", answer: seoFaqText },
    ],
  },
  cta: {
    titleLine1: "Prova Scriptario",
    titleLine2: "gratis",
    subtitleLine1: "15 minuti di trascrizione gratuiti.",
    subtitleLine2: "Nessuna carta, nessun impegno",
    button: "Inizia gratis",
  },
  footer: {
    tagline: "Trascrizione audio e video con IA",
    copyright: "2026 Scriptario. Tutti i diritti riservati.",
    columns: [
      { title: "Prodotto", links: [{ label: "Funzionalità", href: getScriptarioLocalizedPath("/features", locale) }, { label: "Prezzi", href: getScriptarioLocalizedPath("/pricing", locale) }, { label: "Blog", href: getScriptarioLocalizedPath("/blog", locale) }, { label: "Contatti", href: getScriptarioLocalizedPath("/contacts", locale) }] },
      { title: "Supporto", links: [{ label: "WhatsApp", href: "https://wa.me/447732727913?text=Ciao%21%20Ho%20bisogno%20di%20aiuto%20con%20Scriptario." }] },
      { title: "Legale", links: [{ label: "Informativa privacy", href: getScriptarioLocalizedPath("/privacy-policy", locale) }, { label: "Termini di servizio", href: getScriptarioLocalizedPath("/terms", locale) }, { label: "Politica di rimborso", href: getScriptarioLocalizedPath("/refund", locale) }, { label: "Cookie policy", href: getScriptarioLocalizedPath("/cookie-policy", locale) }] },
    ],
  },
});
